/**
 * Client-side validation for a gateway's optional source-IP allowlist
 * (`Gateway.allowed_ips`). This is UX, not a security boundary — the
 * backend revalidates everything (tenancy/ipacl.py via ops/ipacl.py).
 * Mirrors its rules: bare IPs are host routes, networks with host bits
 * set are rejected, and nothing wider than /22 (IPv4) or /64 (IPv6) —
 * `0.0.0.0/0` is "any source" wearing a costume.
 */

export const ALLOWED_IPS_HINT =
  'Leave empty to allow any source (appliances on dynamic IPs). When set, connections AND enrollment only from these ranges. Dual-stack networks: list both families.'

const MAX_PREFIX_V4 = 22
const MAX_PREFIX_V6 = 64

function invalid(entry: string): string {
  return `'${entry}' is not a valid IP or CIDR network`
}

/** Dotted-quad → 32-bit value, or null when malformed. Leading zeros are
 *  rejected, matching Python's ipaddress (the backend validator). */
function parseV4(addr: string): number | null {
  const parts = addr.split('.')
  if (parts.length !== 4) return null
  let value = 0
  for (const part of parts) {
    if (!/^\d{1,3}$/.test(part) || (part.length > 1 && part.startsWith('0'))) return null
    const octet = Number(part)
    if (octet > 255) return null
    value = value * 256 + octet
  }
  return value
}

/** IPv6 address → 128-bit BigInt, or null when malformed. */
function parseV6(addr: string): bigint | null {
  if (addr.includes('%')) return null // zone ids are not allowlist material
  // Platform check: the URL parser only accepts literal IPv6 in brackets.
  try {
    new URL(`http://[${addr}]/`)
  } catch {
    return null
  }
  let text = addr.toLowerCase()
  // An embedded IPv4 tail (`::ffff:203.0.113.7`) counts as two groups.
  const v4Tail = /\d{1,3}(?:\.\d{1,3}){3}$/.exec(text)
  if (v4Tail) {
    const v4 = parseV4(v4Tail[0])
    if (v4 === null) return null
    const hi = Math.floor(v4 / 65536).toString(16)
    const lo = (v4 % 65536).toString(16)
    text = `${text.slice(0, text.length - v4Tail[0].length)}${hi}:${lo}`
  }
  const halves = text.split('::')
  if (halves.length > 2) return null
  const head = halves[0] === '' ? [] : halves[0].split(':')
  const tail = halves.length === 2 && halves[1] !== '' ? halves[1].split(':') : []
  const missing = 8 - head.length - tail.length
  if (halves.length === 1 && missing !== 0) return null // no compression: exactly 8 groups
  if (halves.length === 2 && missing < 1) return null // '::' must compress at least one group
  const groups = [...head, ...Array<string>(missing).fill('0'), ...tail]
  if (groups.length !== 8) return null
  let value = 0n
  for (const group of groups) {
    if (!/^[0-9a-f]{1,4}$/.test(group)) return null
    value = (value << 16n) + BigInt(parseInt(group, 16))
  }
  return value
}

function parsePrefix(raw: string, bits: number): number | null {
  if (!/^\d{1,3}$/.test(raw)) return null
  const prefix = Number(raw)
  return prefix <= bits ? prefix : null
}

/** One entry → an error message, or null when valid. */
export function cidrError(entry: string): string | null {
  const parts = entry.split('/')
  if (parts.length > 2 || parts[0] === '') return invalid(entry)
  const [addr, prefixRaw] = parts

  if (addr.includes(':')) {
    const value = parseV6(addr)
    const prefix = prefixRaw === undefined ? 128 : parsePrefix(prefixRaw, 128)
    if (value === null || prefix === null) return invalid(entry)
    if (prefix < MAX_PREFIX_V6) return `'${entry}' is too wide — /${MAX_PREFIX_V6} is the widest allowed (IPv6)`
    const hostBits = 128 - prefix
    const mask = ((1n << BigInt(prefix)) - 1n) << BigInt(hostBits)
    if ((value & mask) !== value) return `'${entry}' has host bits set — write the network address instead`
    return null
  }

  const value = parseV4(addr)
  const prefix = prefixRaw === undefined ? 32 : parsePrefix(prefixRaw, 32)
  if (value === null || prefix === null) return invalid(entry)
  if (prefix < MAX_PREFIX_V4) return `'${entry}' is too wide — /${MAX_PREFIX_V4} is the widest allowed (IPv4)`
  const hostMask = prefix === 32 ? 0 : 2 ** (32 - prefix) - 1
  if ((value & hostMask) !== 0) return `'${entry}' has host bits set — write the network address instead`
  return null
}

/**
 * The textarea content (one CIDR per line; commas/whitespace tolerated) →
 * the entries to send, or the first validation error.
 */
export function parseAllowedIpsInput(text: string): { cidrs: string[]; error: string | null } {
  const entries = text
    .split(/[\s,]+/)
    .map((s) => s.trim())
    .filter(Boolean)
  for (const entry of entries) {
    const error = cidrError(entry)
    if (error) return { cidrs: [], error }
  }
  return { cidrs: entries, error: null }
}
